import * as Yup from 'yup'
import { IntakeNoteEmployment, IntakeNoteEmploymentStatus, IntakeNoteHealthHistory } from 'src/graphql.generated'

import { BillingCode, BILLING_CODES_REQUIRED_MESSAGE } from './billingCodeConstants'

type ConsentSchema = {
  PARTICIPANT_INELIGIBLE?: boolean
  REVIEWED_CONFIDENTIALITY_AND_EXCEPTIONS?: boolean
  VERBAL_CONSENT_FOR_TREATMENT?: boolean
}

export type IntakeNoteSchema = {
  __version: 3
  CONSENT: ConsentSchema
  OBSTACLES_AND_ACTION_PLAN?: string
  IDENTIFYING_INFO?: string
  EXPECTATIONS_AND_INTERESTS?: string
  EMPLOYMENT?: IntakeNoteEmployment | null
  HEALTH_HISTORY?: IntakeNoteHealthHistory | null
  OBSTACLES?: string | null
  EXPECTED_ACHIEVEMENTS?: Yup.InferType<typeof EXPECTED_ACHIEVEMENTS_SCHEMA> | null
  TREATMENT_GOALS?: string
  SUICIDE_RISK?: string
  INTERNAL_EXTERNAL_STRESSORS?: string
  TIMELINE_OF_SYMPTOMS_AND_TREATMENT?: string
  STARTING_DATE_CONFIRMED?: boolean
  CREATE_QUICK_NOTE?: boolean
  MAJOR_DEPRESSION_EPISODES?: number
  BILLING_CODES?: BillingCode[]
  PARTICIPANT_INELIGIBLE?: boolean
}

export const EMPTY_FIELDS: IntakeNoteSchema = {
  __version: 3,
  CONSENT: {
    REVIEWED_CONFIDENTIALITY_AND_EXCEPTIONS: false,
    VERBAL_CONSENT_FOR_TREATMENT: false,
  },
  BILLING_CODES: [],
}

const CONSENT_SCHEMA = Yup.object({
  REVIEWED_CONFIDENTIALITY_AND_EXCEPTIONS: Yup.boolean().default(false).oneOf([true]),
  VERBAL_CONSENT_FOR_TREATMENT: Yup.boolean().default(false).oneOf([true]),
})
  .default({})
  .required()

// When the complexity of the type definitions for the intake note schema exceeded a certain
// threshold, they seem to expose a TS bug. The complier directive is used as a workaround
// for allowing for otherwise runnable code.
const HEALTH_HISTORY_SCHEMA = Yup.object({
  THERAPY: Yup.boolean().default(false).required(),
  SUICIDALITY: Yup.boolean().default(false).required(),
  GENERALISED_ANXIETY: Yup.boolean().default(false).required(),
  DEPRESSION: Yup.boolean().default(false).required(),
  BIPOLAR: Yup.boolean().default(false).required(),
  PTSD: Yup.boolean().default(false).required(),
  DTD: Yup.boolean().default(false).required(),
  PSYCHOSIS: Yup.boolean().default(false).required(),
  SUBSTANCE_ABUSE: Yup.boolean().default(false).required(),
}).required()

const EXPECTED_ACHIEVEMENTS_SCHEMA = Yup.object({
  CONTROL_EMOTIONS_AND_BEHAVIOUR: Yup.boolean().default(false).required(),
  MOTIVATION_AND_ENERGY: Yup.boolean().default(false).required(),
  OTHER: Yup.boolean().default(false).required(),
  OTHER_REASON: Yup.string().nullable(),
  SELF_ESTEEM_AND_CONFIDENCE: Yup.boolean().default(false).required(),
  SENSE_OF_CALM: Yup.boolean().default(false).required(),
}).default(undefined)

const EMPLOYMENT_SCHEMA = Yup.object({
  STATUS: Yup.string().oneOf(Object.values(IntakeNoteEmploymentStatus)).nullable(),
})
  .default({})
  .required()

const BILLING_CODES_SCHEMA = Yup.array().default([])

const REQUIRED_BILLING_CODES_SCHEMA = BILLING_CODES_SCHEMA.min(1, BILLING_CODES_REQUIRED_MESSAGE)

/**
 * Marking a participant ineligible for the program does not relax the note: every field except the
 * "eligible" confirmation stays as required as it is for an eligible participant, diagnosis codes
 * included, because billing cannot generate a claim without one and the note is part of the clinical
 * record either way. Self-pay
 * participants remain exempt from the diagnosis code requirement (there is no claim to generate)
 * via `requireBillingCodes`.
 */
export const createIntakeNoteSchema = ({ requireBillingCodes = false } = {}) =>
  Yup.object({
    __version: Yup.mixed<3>().oneOf([3]).default(3),
    PARTICIPANT_INELIGIBLE: Yup.boolean().default(false).required(),
    CONSENT: CONSENT_SCHEMA,
    HEALTH_HISTORY: HEALTH_HISTORY_SCHEMA,
    EXPECTED_ACHIEVEMENTS: EXPECTED_ACHIEVEMENTS_SCHEMA.nullable(),
    EMPLOYMENT: EMPLOYMENT_SCHEMA,
    TIMELINE_OF_SYMPTOMS_AND_TREATMENT: Yup.string().required(),
    OBSTACLES: Yup.string().nullable(),
    IDENTIFYING_INFO: Yup.string().required(),
    EXPECTATIONS_AND_INTERESTS: Yup.string().required(),
    TREATMENT_GOALS: Yup.string().optional(),
    SUICIDE_RISK: Yup.string().required(),
    OBSTACLES_AND_ACTION_PLAN: Yup.string().optional(),
    STARTING_DATE_CONFIRMED: Yup.boolean().when('PARTICIPANT_INELIGIBLE', {
      is: true,
      then: (s) => s.notRequired(),
      otherwise: (s) => s.oneOf([true]).required(),
    }),
    CREATE_QUICK_NOTE: Yup.boolean().oneOf([true]).required(),
    MAJOR_DEPRESSION_EPISODES: Yup.number().required(),

    BILLING_CODES: requireBillingCodes ? REQUIRED_BILLING_CODES_SCHEMA : BILLING_CODES_SCHEMA,
  }).required()

/** Casting and defaults only — use `createIntakeNoteSchema` when validating a submitted form. */
export const INTAKE_NOTE_SCHEMA = createIntakeNoteSchema()

export type FormikContext = {
  value?: IntakeNoteSchema
}
